import { useAmplitude } from "@getsoren/shared-module";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { createVisibleStopwatch, PAGE_LEFT_EVENT, pageLeftProperties } from "@/utils/pageDuration";

/**
 * Reports how long each page was visible, as a "Page Left" event sent when the user leaves it:
 * on route change, when the tab is closed and when it goes to the background (the SDK flushes on
 * page hide, so the last event of a closed tab is best effort). Mounted once inside the router.
 * The effect is keyed on the pathname only: `track` is read through a ref so that a re-render of
 * the tree never splits a visit into several events.
 */
const PageLeftTracker = () => {
  const { pathname } = useLocation();
  const { track } = useAmplitude();
  const trackRef = useRef(track);
  trackRef.current = track;

  useEffect(() => {
    let watch = createVisibleStopwatch(Date.now(), document.visibilityState === "visible");
    let sent = false;

    const send = () => {
      if (sent) {
        return;
      }

      sent = true;
      const properties = pageLeftProperties(pathname, watch.stop(Date.now()));

      if (properties) {
        trackRef.current(PAGE_LEFT_EVENT, properties);
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        watch.pause(Date.now());
      } else {
        watch.resume(Date.now());
      }
    };

    // A page restored from the back/forward cache is a new visit: the previous one was sent on pagehide.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        watch = createVisibleStopwatch(Date.now(), document.visibilityState === "visible");
        sent = false;
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", send);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", send);
      window.removeEventListener("pageshow", handlePageShow);
      send();
    };
  }, [pathname]);

  return null;
};

export default PageLeftTracker;
